import React from "react";
import { Avatar, Text } from "@flixbus/honeycomb-react";
import {
  Icon,
  IconProfileSolid,
  IconDocumentSolid,
  IconSpark,
} from "@flixbus/honeycomb-icons-react";
import type { ChatMessage as ChatMessageModel } from "./chatMessages";
import AssistantBody from "./AssistantBody.component";
import styles from "./ChatMessage.module.scss";

export interface ChatMessageProps {
  message: ChatMessageModel;
  onRetry: () => void;
}

/** One chat bubble. User messages align right (green); assistant messages align
 *  left and render the phase-specific content (welcome / progress / result). */
const ChatMessage: React.FC<ChatMessageProps> = ({ message, onRetry }) => {
  if (message.role === "user") {
    // Scope card: the wizard "header" as labelled label→value rows, so the
    // opening context is readable rather than a cramped dot-joined string.
    if ("kind" in message && message.kind === "scope") {
      return (
        <div className={styles.userRow}>
          <dl className={styles.scopeCard}>
            {message.fields.map((f) => (
              <div key={f.label} className={styles.scopeRow}>
                <dt className={styles.scopeLabel}>{f.label}</dt>
                <dd className={styles.scopeValue}>{f.value}</dd>
              </div>
            ))}
          </dl>
          <Avatar extraClasses={styles.userAvatar} size={6}>
            <Icon
              InlineIcon={IconProfileSolid}
              appearance="secondary"
              aria-hidden="true"
            />
          </Avatar>
        </div>
      );
    }

    return (
      <div className={styles.userRow}>
        <div className={styles.userStack}>
          {message.files.map((f) => (
            <div key={f.id} className={styles.fileChip}>
              <Icon InlineIcon={IconDocumentSolid} aria-hidden="true" />
              <Text Elem="span" small extraClasses={styles.fileChipName}>
                {f.name}
              </Text>
            </div>
          ))}
          {message.text.trim() !== "" && (
            <div className={styles.userBubble}>
              <Text extraClasses={styles.bubbleText}>{message.text}</Text>
            </div>
          )}
        </div>
        <Avatar extraClasses={styles.userAvatar} size={6}>
          <Icon
            InlineIcon={IconProfileSolid}
            appearance="secondary"
            aria-hidden="true"
          />
        </Avatar>
      </div>
    );
  }

  return (
    <div className={styles.assistantRow}>
      <Avatar extraClasses={styles.assistantAvatar} size={6}>
        <Icon InlineIcon={IconSpark} size="cover" aria-hidden="true" />
      </Avatar>
      <div className={styles.assistantBubble}>
        <AssistantBody message={message} onRetry={onRetry} />
      </div>
    </div>
  );
};

export default React.memo(ChatMessage);
